import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  User as UserIcon, 
  Mail, 
  Shield, 
  Building2, 
  Lock, 
  Key, 
  Bell, 
  Settings,
  LogOut,
  ChevronRight,
  Globe,
  Database,
  Fingerprint
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../hooks/useRole';
import { cn } from '../../lib/utils';

const Drawer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 480px;
  max-width: 95vw;
  height: 100vh;
  background: #0f172a;
  box-shadow: -20px 0 50px rgba(0, 0, 0, 0.5);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(255, 255, 255, 0.05);
`;

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
`;

const UserProfileDrawer: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const { userRole } = useRole();

  if (!user) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <Drawer
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          >
            {/* Header */}
            <div className="p-8 bg-gradient-to-br from-slate-900 to-slate-950 border-b border-white/5">
              <div className="flex justify-between items-start mb-8">
                <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-2xl shadow-blue-500/20">
                  <UserIcon className="w-10 h-10 text-white" />
                </div>
                <button 
                  onClick={onClose}
                  className="p-2 rounded-xl bg-white/5 text-slate-500 hover:text-white transition-colors border border-white/5"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <h2 className="text-2xl font-bold text-white tracking-tight mb-1">{user.fullName}</h2>
              <div className="flex items-center gap-2 text-slate-500 text-sm font-medium mb-6">
                <Mail className="w-3.5 h-3.5" />
                {user.email || `${user.username}@axiomflow.com`}
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-[10px] font-bold text-blue-400 uppercase tracking-widest">
                  {userRole}
                </span>
                <span className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-[10px] font-bold text-emerald-400 uppercase tracking-widest">
                  Active Session
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8 space-y-8">
              <section>
                <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.2em] mb-4">Account Metadata</h3>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { label: 'Organization', value: user.tenantName || 'Standard Tier', icon: <Building2 /> },
                    { label: 'Primary Region', value: 'US East (N. Virginia)', icon: <Globe /> },
                    { label: 'Assigned Role', value: userRole, icon: <Shield /> },
                    { label: 'Data Access', value: 'Full Authorization', icon: <Database /> }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-slate-900/50 border border-white/5 rounded-2xl">
                      <div className="flex items-center gap-3">
                        <div className="text-slate-500">{item.icon}</div>
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{item.label}</span>
                      </div>
                      <span className="text-sm font-semibold text-slate-200">{item.value}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.2em] mb-4">Security Settings</h3>
                <div className="space-y-3">
                  <button className="w-full flex items-center justify-between p-4 bg-slate-900 border border-white/5 rounded-2xl hover:bg-slate-800 transition-all group text-left">
                    <div className="flex items-center gap-3">
                      <Fingerprint className="w-5 h-5 text-emerald-400" />
                      <div>
                        <div className="text-sm font-bold text-white">MFA Authentication</div>
                        <div className="text-[10px] text-slate-500 font-medium uppercase mt-0.5">Secure your session with 2FA</div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors" />
                  </button>
                  <button className="w-full flex items-center justify-between p-4 bg-slate-900 border border-white/5 rounded-2xl hover:bg-slate-800 transition-all group text-left">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-blue-400" />
                      <div>
                        <div className="text-sm font-bold text-white">Reset Password</div>
                        <div className="text-[10px] text-slate-500 font-medium uppercase mt-0.5">Update institutional credentials</div>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors" />
                  </button>
                </div>
              </section>
            </div>

            {/* Footer */}
            <div className="p-8 mt-auto border-t border-white/5 bg-slate-950/50">
              <button 
                onClick={() => { logout(); onClose(); }}
                className="w-full py-4 bg-red-500/10 border border-red-500/20 text-red-400 font-bold rounded-2xl hover:bg-red-500/20 transition-all flex items-center justify-center gap-3 group"
              >
                <LogOut className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                Terminate Session
              </button>
              <div className="mt-6 text-center">
                <p className="text-[10px] text-slate-600 font-bold uppercase tracking-widest">Axiom institutional OS V4.2.0</p>
              </div>
            </div>
          </Drawer>
        </>
      )}
    </AnimatePresence>
  );
};

export default UserProfileDrawer;
